import { useUser } from '@clerk/nextjs';
import { useCallback } from 'react';

import { postMessageRN } from 'utils/webview.utils';

import { useEmbedded } from 'hooks/useEmbedded';
import {
  magicLinkSent,
  saudiRewardsLinked,
  userAuthenticationView,
  UserAuthenticationViewProperties,
  userOnboardingStart,
  userOnboardingSuccess,
  userUpdate,
} from 'services/avo';
import { useTrackEvent, useTrackEventOnce } from 'services/datalayer/hooks';

export const useUserAuthenticationView = (
  params: Partial<UserAuthenticationViewProperties>,
) =>
  useTrackEventOnce({
    trackFn: userAuthenticationView,
    variables: { ...params },
    withOrigin: true,
  });

export const useTrackMagicLinkSent = () =>
  useTrackEvent({
    trackFn: magicLinkSent,
    variables: {},
  });

const useUserLoginMethod = () => {
  const { user, isLoaded } = useUser();

  if (!isLoaded) return undefined;
  const providers = user?.verifiedExternalAccounts.map(
    (account) => account.provider,
  );
  if (!providers?.length) return 'Email';
  if (providers?.includes('apple')) return 'Apple';
  if (providers?.includes('google')) return 'Google';
};

export const useTrackOnboardingStart = () => {
  const method = useUserLoginMethod();

  useTrackEventOnce({
    trackFn: userOnboardingStart,
    variables: {
      method,
    },
  });
};

export const useTrackOnboardingSuccess = () => {
  const method = useUserLoginMethod();

  return useTrackEvent({
    trackFn: userOnboardingSuccess,
    variables: { method },
  });
};

export const useTrackUserUpdate = () =>
  useTrackEvent({ trackFn: userUpdate, variables: {} });

export const useTrackSaudiRewardsLinked = () => {
  const { isEmbedded } = useEmbedded();
  const track = useTrackEvent({ trackFn: saudiRewardsLinked, variables: {} });

  return useCallback(() => {
    if (isEmbedded) {
      postMessageRN({
        type: 'analytics-saudi-rewards-linked',
      });
    } else {
      track({});
    }
  }, [isEmbedded, track]);
};
